import axios from "axios"
import { saveAs } from "file-saver"

const apiUrl = "/api/"

const MIME_BY_EXTENSION = {
  "3gp": "video/3gpp",
  aac: "audio/aac",
  avi: "video/x-msvideo",
  csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  flac: "audio/flac",
  gif: "image/gif",
  heic: "image/heic",
  heif: "image/heif",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  m4a: "audio/mp4",
  m4v: "video/x-m4v",
  md: "text/markdown",
  mov: "video/quicktime",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
  odp: "application/vnd.oasis.opendocument.presentation",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
  odt: "application/vnd.oasis.opendocument.text",
  ogg: "audio/ogg",
  pdf: "application/pdf",
  png: "image/png",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  rtf: "application/rtf",
  txt: "text/plain",
  wav: "audio/wav",
  webm: "video/webm",
  webp: "image/webp",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  zip: "application/zip",
}

function getFileMimeType(file) {
  const provided = String((file && file.type) || "")
    .trim()
    .toLowerCase()
  if (provided && provided !== "application/octet-stream") return provided
  const extension = String((file && file.name) || "")
    .split(".")
    .pop()
    .toLowerCase()
  return MIME_BY_EXTENSION[extension] || provided
}

function buildUrl(path) {
  return `${apiUrl}${path}`
}

function buildOptions(options = {}, signal = null) {
  if (!options.headers) {
    options.headers = {}
  }
  if (signal) options.signal = signal
  options.headers.Authorization = localStorage.getItem("token")
  return options
}

function hasFlutterDownloadBridge() {
  return (
    typeof window !== "undefined" &&
    window.flutter_inappwebview &&
    typeof window.flutter_inappwebview.callHandler === "function"
  )
}

function getDownloadParts(filename) {
  const safeFilename = String(filename || "download")
    .replace(/[\\/\0]/g, "_")
    .trim()
  const normalizedFilename = safeFilename || "download"
  const dotIndex = normalizedFilename.lastIndexOf(".")
  const hasExtension = dotIndex > 0 && dotIndex < normalizedFilename.length - 1
  const extension = (hasExtension ? normalizedFilename.slice(dotIndex + 1) : "bin").replace(/[^a-z0-9]/gi, "").toLowerCase() || "bin"
  const name = (hasExtension ? normalizedFilename.slice(0, dotIndex) : normalizedFilename).trim() || "download"
  return { extension, name }
}

function sendBlobToFlutter(blob, filename) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error || new Error("Die Datei konnte nicht für die App vorbereitet werden."))
    reader.onabort = () => reject(new Error("Die Dateivorbereitung wurde abgebrochen."))
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : ""
      const base64 = dataUrl.split(",")[1]
      if (!base64) {
        reject(new Error("Die Datei konnte nicht für die App vorbereitet werden."))
        return
      }

      const { extension, name } = getDownloadParts(filename)
      try {
        Promise.resolve(window.flutter_inappwebview.callHandler("blobToBase64Handler", base64, extension, name))
          .then((result) => {
            if (result === false || (result && result.success === false)) {
              reject(new Error("Die App konnte die Datei nicht öffnen."))
              return
            }
            resolve(result)
          })
          .catch(reject)
      } catch (error) {
        reject(error)
      }
    }
    reader.readAsDataURL(blob)
  })
}

function saveBlobInBrowser(blob, filename) {
  const downloadUrl = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = downloadUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(downloadUrl), 5000)
}

export default {
  akademie: {
    // Modules
    fetchModules(params = {}) {
      const url = buildUrl("akademie/modules")
      return axios.get(url, buildOptions({ params })).then((res) => res.data)
    },
    fetchModule(id) {
      const url = buildUrl(`akademie/modules/${id}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    createModule(data) {
      const url = buildUrl("akademie/modules")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    updateModule(id, data) {
      const url = buildUrl(`akademie/modules/${id}`)
      return axios.put(url, data, buildOptions()).then((res) => res.data)
    },
    deleteModule(id) {
      const url = buildUrl(`akademie/modules/${id}`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },
    prepareModuleMediaUpload(moduleId, purpose, file) {
      const url = buildUrl(`akademie/modules/${moduleId}/media/uploads`)
      return axios
        .post(
          url,
          {
            purpose,
            fileName: file.name,
            mimeType: getFileMimeType(file),
            size: file.size,
          },
          buildOptions(),
        )
        .then((res) => res.data)
    },
    cancelModuleMediaUpload(moduleId, purpose, media) {
      const url = buildUrl(`akademie/modules/${moduleId}/media/uploads`)
      return axios.delete(url, buildOptions({ data: { purpose, media } })).then((res) => res.data)
    },
    uploadLesson(moduleId, data) {
      const url = buildUrl(`akademie/modules/${moduleId}/lessons`)
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    updateLesson(lessonId, data) {
      const url = buildUrl(`akademie/lessons/${lessonId}`)
      return axios.put(url, data, buildOptions()).then((res) => res.data)
    },
    deleteLesson(lessonId) {
      const url = buildUrl(`akademie/lessons/${lessonId}`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },
    // Pfade (ohne Token!) für SecuredMedia.vue – das nimmt sich den Token aus dem Authorization-Header
    getLessonStreamPath(lessonId) {
      return buildUrl(`akademie/lessons/${lessonId}/stream`)
    },
    getLessonPlaybackPath(lessonId) {
      return buildUrl(`akademie/lessons/${lessonId}/playback`)
    },
    getToolboxItemPath(moduleId, itemId) {
      return buildUrl(`akademie/toolbox/${moduleId}/${itemId}`)
    },
    getToolboxItemPlaybackPath(moduleId, itemId) {
      return buildUrl(`akademie/toolbox/${moduleId}/${itemId}/playback`)
    },
    getIdeaAttachmentPath(ideaId, fileId) {
      return buildUrl(`akademie/ideas/${ideaId}/attachments/${encodeURIComponent(fileId)}`)
    },
    getIdeaAttachmentPlaybackPath(ideaId, fileId) {
      return buildUrl(`akademie/ideas/${ideaId}/attachments/${encodeURIComponent(fileId)}/playback`)
    },
    reorderModules(tier, orderedIds, track = "license") {
      const url = buildUrl("akademie/modules/reorder")
      return axios.post(url, { tier, track, orderedIds }, buildOptions()).then((res) => res.data)
    },
    reorderLessons(moduleId, orderedIds) {
      const url = buildUrl(`akademie/modules/${moduleId}/lessons/reorder`)
      return axios.post(url, { orderedIds }, buildOptions()).then((res) => res.data)
    },

    // Toolbox
    uploadToolboxItem(moduleId, data) {
      const url = buildUrl(`akademie/modules/${moduleId}/toolbox`)
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    updateToolboxItem(moduleId, itemId, data) {
      const url = buildUrl(`akademie/modules/${moduleId}/toolbox/${itemId}`)
      return axios.put(url, data, buildOptions()).then((res) => res.data)
    },
    deleteToolboxItem(moduleId, itemId) {
      const url = buildUrl(`akademie/modules/${moduleId}/toolbox/${itemId}`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },
    // Download eines beliebigen Blob-Endpoints (Browser + Flutter InAppWebView)
    downloadFile(path, filename = "download") {
      const url = path.startsWith("http") || path.startsWith("/") ? path : buildUrl(path)
      return axios.get(url, { responseType: "blob", headers: { Authorization: localStorage.getItem("token") } }).then((response) => {
        const blob = response.data
        if (hasFlutterDownloadBridge()) return sendBlobToFlutter(blob, filename)
        saveBlobInBrowser(blob, filename)
        return undefined
      })
    },
    downloadToolboxItem(moduleId, itemId, filename) {
      return this.downloadFile(`akademie/toolbox/${moduleId}/${itemId}?download=1`, filename || "datei")
    },
    downloadLessonMedia(lessonId, filename) {
      return this.downloadFile(`akademie/lessons/${lessonId}/stream?download=1`, filename || "datei")
    },
    downloadIdeaAttachment(ideaId, fileId, filename) {
      return this.downloadFile(`akademie/ideas/${ideaId}/attachments/${encodeURIComponent(fileId)}`, filename || "datei")
    },

    saveQuiz(moduleId, data) {
      const url = buildUrl(`akademie/modules/${moduleId}/quiz`)
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },

    // Progress
    fetchProgress(params = {}) {
      const url = buildUrl("akademie/progress")
      return axios.get(url, buildOptions({ params })).then((res) => res.data)
    },
    trackLessonProgress(moduleId, lessonId, progressSeconds) {
      const url = buildUrl(`akademie/progress/${moduleId}/lessons/${lessonId}`)
      return axios.post(url, { progressSeconds }, buildOptions()).then((res) => res.data)
    },
    completeLesson(moduleId, lessonId) {
      const url = buildUrl(`akademie/progress/${moduleId}/lessons/${lessonId}/complete`)
      return axios.post(url, {}, buildOptions()).then((res) => res.data)
    },
    submitQuiz(moduleId, answers) {
      const url = buildUrl(`akademie/progress/${moduleId}/quiz/attempt`)
      return axios.post(url, { answers }, buildOptions()).then((res) => res.data)
    },

    // Leadership coaching
    fetchLeadershipModules() {
      return this.fetchModules({ track: "leadership" })
    },
    fetchLeadershipSubmission(moduleId) {
      const url = buildUrl("akademie/leadership/submissions/me")
      return axios.get(url, buildOptions({ params: { moduleId } })).then((res) => res.data)
    },
    startLeadershipSubmissionUpload(moduleId, data) {
      const url = buildUrl(`akademie/leadership/modules/${moduleId}/uploads/stream`)
      return axios.post(url, { fileName: data.fileName, mimeType: data.mimeType, size: data.fileSize }, buildOptions()).then((res) => res.data)
    },
    completeLeadershipSubmissionUpload(moduleId, streamUid, data = {}) {
      const url = buildUrl(`akademie/leadership/modules/${moduleId}/uploads/stream/${streamUid}/complete`)
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    cancelLeadershipSubmissionUpload(moduleId, streamUid, sessionToken) {
      const url = buildUrl(`akademie/leadership/modules/${moduleId}/uploads/stream/${streamUid}`)
      return axios.delete(url, buildOptions({ data: { sessionToken } })).then((res) => res.data)
    },
    getLeadershipSubmissionPlaybackPath(submissionId) {
      return buildUrl(`akademie/leadership/submissions/${submissionId}/playback`)
    },
    fetchLeadershipSubmissions(params = {}) {
      const url = buildUrl("akademie/leadership/admin/submissions")
      return axios.get(url, buildOptions({ params })).then((res) => res.data)
    },
    reviewLeadershipSubmission(submissionId, data) {
      const url = buildUrl(`akademie/leadership/admin/submissions/${submissionId}/review`)
      return axios.put(url, data, buildOptions()).then((res) => res.data)
    },

    // Leadership coaching 1:1
    fetchLeadershipCoachingSlots(params = {}) {
      const url = buildUrl("akademie/leadership/coaching/availability")
      return axios.get(url, buildOptions({ params })).then((res) => res.data)
    },
    fetchMyLeadershipCoachingBookings() {
      const url = buildUrl("akademie/leadership/coaching/bookings/me")
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    bookLeadershipCoachingSlot(startsAt, data) {
      const url = buildUrl("akademie/leadership/coaching/bookings")
      return axios.post(url, { startsAt, ...data }, buildOptions()).then((res) => res.data)
    },
    cancelLeadershipCoachingBooking(bookingId) {
      const url = buildUrl(`akademie/leadership/coaching/bookings/${bookingId}`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },
    createLeadershipExtraRequest(data) {
      const url = buildUrl("akademie/leadership/coaching/extra-requests")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    fetchMyLeadershipExtraRequests() {
      const url = buildUrl("akademie/leadership/coaching/extra-requests/me")
      return axios.get(url, buildOptions()).then((res) => res.data)
    },

    // Leadership administration
    fetchLeadershipCoachingSchedule() {
      const url = buildUrl("akademie/leadership/admin/coaching/schedule")
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    updateLeadershipCoachingSchedule(data) {
      const url = buildUrl("akademie/leadership/admin/coaching/schedule")
      return axios.put(url, data, buildOptions()).then((res) => res.data)
    },
    fetchLeadershipContact() {
      const url = buildUrl("akademie/leadership/admin/contact")
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    validateLeadershipContact(userId) {
      const url = buildUrl(`akademie/leadership/admin/contact/validate/${encodeURIComponent(userId)}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    updateLeadershipContact(userId) {
      const url = buildUrl("akademie/leadership/admin/contact")
      return axios.put(url, { userId }, buildOptions()).then((res) => res.data)
    },
    fetchLeadershipCoachingExceptions(params = {}) {
      const url = buildUrl("akademie/leadership/admin/coaching/exceptions")
      return axios.get(url, buildOptions({ params })).then((res) => res.data)
    },
    createLeadershipCoachingException(data) {
      const url = buildUrl("akademie/leadership/admin/coaching/exceptions")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    deleteLeadershipCoachingException(exceptionId) {
      const url = buildUrl(`akademie/leadership/admin/coaching/exceptions/${exceptionId}`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },
    fetchLeadershipCoachingBookings(params = {}) {
      const url = buildUrl("akademie/leadership/admin/coaching/bookings")
      return axios.get(url, buildOptions({ params })).then((res) => res.data)
    },
    retryLeadershipCoachingMeeting(bookingId) {
      const url = buildUrl(`akademie/leadership/admin/coaching/bookings/${bookingId}/retry-meeting`)
      return axios.post(url, {}, buildOptions()).then((res) => res.data)
    },
    fetchLeadershipExtraRequests(params = {}) {
      const url = buildUrl("akademie/leadership/admin/coaching/extra-requests")
      return axios.get(url, buildOptions({ params })).then((res) => res.data)
    },
    updateLeadershipExtraRequest(requestId, data) {
      const url = buildUrl(`akademie/leadership/admin/coaching/extra-requests/${requestId}`)
      return axios.put(url, data, buildOptions()).then((res) => res.data)
    },

    // Licenses
    fetchMyLicenses() {
      const url = buildUrl("akademie/licenses/me")
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    fetchAllLicenses(params = {}) {
      const url = buildUrl("akademie/licenses")
      return axios.get(url, buildOptions({ params })).then((res) => res.data)
    },
    fetchExpiringLicenses() {
      const url = buildUrl("akademie/licenses/expiring")
      return axios.get(url, buildOptions()).then((res) => res.data)
    },

    // Termine
    fetchTerminTeamStatus(terminId) {
      const url = buildUrl(`akademie/termine/${terminId}/team-status`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    // Für das manuelle Hinzufügen (Teacher meldet Mitarbeiter an)
    adminAddRegistration(terminId, userId) {
      const url = buildUrl(`akademie/termine/${terminId}/admin-add`)
      return axios.post(url, { userId }, buildOptions()).then((res) => res.data)
    },
    fetchTermine() {
      const url = buildUrl("akademie/termine")
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    fetchTrainingProtocols() {
      const url = buildUrl("akademie/training-protocols")
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    fetchTrainingProtocol(sessionId) {
      const url = buildUrl(`akademie/termine/${sessionId}/protocol`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    prepareTrainingProtocolMediaUpload(sessionId, file) {
      const url = buildUrl(`akademie/termine/${sessionId}/protocol/media/uploads`)
      return axios
        .post(
          url,
          {
            fileName: file.name,
            mimeType: getFileMimeType(file),
            size: file.size,
          },
          buildOptions(),
        )
        .then((res) => res.data)
    },
    cancelTrainingProtocolMediaUpload(sessionId, media) {
      const url = buildUrl(`akademie/termine/${sessionId}/protocol/media/uploads`)
      return axios.delete(url, buildOptions({ data: { media } })).then((res) => res.data)
    },
    saveTrainingProtocol(sessionId, data) {
      const url = buildUrl(`akademie/termine/${sessionId}/protocol`)
      return axios.put(url, data, buildOptions()).then((res) => res.data)
    },
    deleteTrainingProtocol(sessionId) {
      const url = buildUrl(`akademie/termine/${sessionId}/protocol`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },
    getTrainingProtocolFilePath(sessionId, fileId) {
      return buildUrl(`akademie/termine/${sessionId}/protocol/files/${encodeURIComponent(fileId)}`)
    },
    getTrainingProtocolPreviewPath(sessionId, fileId, variant = "large") {
      const size = variant === "thumbnail" ? "thumbnail" : "large"
      return buildUrl(`akademie/termine/${sessionId}/protocol/files/${encodeURIComponent(fileId)}/preview?variant=${size}`)
    },
    downloadTrainingProtocolFile(sessionId, fileId, filename) {
      const path = `akademie/termine/${sessionId}/protocol/files/${encodeURIComponent(fileId)}?download=1`
      return this.downloadFile(path, filename || "datei")
    },
    fetchTermin(id) {
      const url = buildUrl(`akademie/termine/${id}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    createTermin(data) {
      const url = buildUrl("akademie/termine")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    updateTermin(id, data) {
      const url = buildUrl(`akademie/termine/${id}`)
      return axios.put(url, data, buildOptions()).then((res) => res.data)
    },
    deleteTermin(id) {
      const url = buildUrl(`akademie/termine/${id}`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },
    registerForTermin(id, data = {}) {
      const url = buildUrl(`akademie/termine/${id}/register`)
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    cancelRegistration(regId, reason) {
      const url = buildUrl(`akademie/registrations/${regId}/cancel`)
      return axios.post(url, { reason }, buildOptions()).then((res) => res.data)
    },
    fetchRegistrations(terminId) {
      const url = buildUrl(`akademie/termine/${terminId}/registrations`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    markAttendance(terminId, regId, attended) {
      const url = buildUrl(`akademie/termine/${terminId}/registrations/${regId}/attendance`)
      return axios.post(url, { attended }, buildOptions()).then((res) => res.data)
    },
    adminCancelRegistration(terminId, regId, reason) {
      const url = buildUrl(`akademie/termine/${terminId}/registrations/${regId}/admin-cancel`)
      return axios.post(url, { reason }, buildOptions()).then((res) => res.data)
    },
    exportTerminExcel(terminId, terminTitle = "Teilnehmerliste") {
      const url = buildUrl(`akademie/termine/${terminId}/export`)

      return axios
        .get(url, {
          responseType: "blob",
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        })
        .then((response) => {
          const filename = `${terminTitle.replace(/\s+/g, "_")}.xlsx`

          if (window.flutter_inappwebview) {
            // App-Modus: Base64 an Flutter Bridge senden
            const reader = new FileReader()
            reader.readAsDataURL(response.data)
            reader.onloadend = function () {
              const base64 = reader.result.split(",")[1]
              window.flutter_inappwebview.callHandler("blobToBase64Handler", base64.toString(), "xlsx", filename)
            }
          } else {
            // Browser-Modus
            saveAs(response.data, filename)
          }
        })
    },

    // Ideas (Kreativwerkstatt)
    fetchIdeas(moduleId) {
      const url = buildUrl("akademie/ideas")
      return axios.get(url, buildOptions({ params: { moduleId } })).then((res) => res.data)
    },
    fetchIdea(id) {
      const url = buildUrl(`akademie/ideas/${id}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    prepareIdeaMediaUpload(moduleId, file) {
      const url = buildUrl(`akademie/ideas/modules/${moduleId}/media/uploads`)
      return axios
        .post(
          url,
          {
            fileName: file.name,
            mimeType: getFileMimeType(file),
            size: file.size,
          },
          buildOptions(),
        )
        .then((res) => res.data)
    },
    cancelIdeaMediaUpload(moduleId, media) {
      const url = buildUrl(`akademie/ideas/modules/${moduleId}/media/uploads`)
      return axios.delete(url, buildOptions({ data: { media } })).then((res) => res.data)
    },
    createIdea(data) {
      const url = buildUrl("akademie/ideas")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    updateIdea(id, data) {
      const url = buildUrl(`akademie/ideas/${id}`)
      return axios.put(url, data, buildOptions()).then((res) => res.data)
    },
    deleteIdea(id) {
      const url = buildUrl(`akademie/ideas/${id}`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },
    toggleLike(id) {
      const url = buildUrl(`akademie/ideas/${id}/like`)
      return axios.post(url, {}, buildOptions()).then((res) => res.data)
    },
    addComment(id, text) {
      const url = buildUrl(`akademie/ideas/${id}/comments`)
      return axios.post(url, { text }, buildOptions()).then((res) => res.data)
    },
    deleteComment(ideaId, commentId) {
      const url = buildUrl(`akademie/ideas/${ideaId}/comments/${commentId}`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },

    // Teacher
    fetchTeacherUserList(params = {}) {
      const url = buildUrl("akademie/teacher/users")
      return axios.get(url, buildOptions({ params })).then((res) => res.data)
    },
    fetchUserKartei(userId) {
      const url = buildUrl(`akademie/teacher/users/${userId}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    addTeacherNote(userId, text) {
      const url = buildUrl(`akademie/teacher/users/${userId}/notes`)
      return axios.post(url, { text }, buildOptions()).then((res) => res.data)
    },
    updateTeacherNote(noteId, text) {
      const url = buildUrl(`akademie/teacher/notes/${noteId}`)
      return axios.put(url, { text }, buildOptions()).then((res) => res.data)
    },
    deleteTeacherNote(noteId) {
      const url = buildUrl(`akademie/teacher/notes/${noteId}`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },

    // Reports
    fetchLicenseOverview(params = {}) {
      const url = buildUrl("akademie/reports/license-overview")
      return axios.get(url, buildOptions({ params })).then((res) => res.data)
    },
    exportLicenseOverviewExcel(params = {}) {
      const url = buildUrl("akademie/reports/license-overview/excel")
      return axios.get(url, { ...buildOptions({ params }), responseType: "blob" }).then((response) => {
        const blob = new Blob([response.data], { type: response.headers["content-type"] })
        saveAs(blob, "lizenzen.xlsx")
      })
    },
  },
}
